/**
 * Ticket List Component
 * Displays a list of tickets with loading and empty states
 */

import { useEffect, useState } from 'react'
import { Box, Typography, CircularProgress, Alert, TablePagination } from '@mui/material'
import { ConfirmationNumber } from '@mui/icons-material'
import { TicketSummaryDto } from '@api/types/tickets'
import { TicketCard } from './TicketCard'

interface TicketListProps {
  tickets: TicketSummaryDto[]
  loading?: boolean
  error?: string | null
  emptyMessage?: string
  onTicketClick?: (ticketId: string) => void
  showAssignee?: boolean
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export function TicketList({
  tickets,
  loading = false,
  error = null,
  emptyMessage = 'No tickets found',
  onTicketClick,
  showAssignee = false,
}: TicketListProps) {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  // A fresh list (a tab switch, a refresh that shrank the result set) may not have this page any
  // more. Clamp to the last page that exists rather than resetting to the first: keyed on the
  // array's identity, a reset fired on EVERY refetch — so claiming a ticket on page 3 threw the
  // moderator back to page 1, and Refresh did the same. The length is what actually decides
  // whether the current page is still real.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(tickets.length / pageSize) - 1)
    setPage((current) => Math.min(current, lastPage))
  }, [tickets.length, pageSize])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    )
  }

  if (tickets.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          py: 4,
        }}
      >
        <ConfirmationNumber sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    )
  }

  const start = page * pageSize
  const pageTickets = tickets.slice(start, start + pageSize)

  return (
    <Box>
      {pageTickets.map((ticket) => (
        <TicketCard key={ticket.id} ticket={ticket} onClick={onTicketClick} showAssignee={showAssignee} />
      ))}
      <TablePagination
        component="div"
        count={tickets.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          setPageSize(parseInt(e.target.value, 10))
          setPage(0)
        }}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        sx={{
          '& .MuiTablePagination-toolbar': { px: { xs: 0, sm: 2 }, flexWrap: 'wrap' },
        }}
      />
    </Box>
  )
}
